import { Icon } from '@iconify/react';
import { Button, IconButton } from '@mui/material';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useAuth } from 'modules/auth/auth.context';
import {
  CollapsibleCard,
  ExpandableTable,
  MonthYearPickerAction,
  SectionTitle,
  StyledGrid,
  StyledGridItem,
} from 'modules/shared/components';
import { PRIORITY_COLORS, PRIORITY_LABELS } from 'modules/shared/constants';
import useNow from 'modules/shared/hooks/useNow';
import {
  buildMonthWeeks,
  formatOrdinalDay,
  formatTimeString,
  getIsoWeek,
  isPastMonth,
} from 'modules/shared/utils';
import { TASK_STATUS } from 'modules/work-implementation/constants';
import { TaskName } from 'modules/work-implementation/project-view/shared/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logger from 'utils/logger';
import useEmployeeProjectList from '../hooks/useEmployeeProjectList';
import { updateTaskStatus } from '../service';
import {
  accumulateTimeUsed,
  calculateProjectTimeLeft,
  formatTimeLeft,
  isTaskRunning,
  isTaskTerminal,
} from '../utils';

function getProjectListColumns() {
  return [
    { id: 'code', label: 'Job Code', dataKey: 'code', sortable: true },
    {
      id: 'name',
      label: 'Project Name',
      dataKey: 'name',
      sortable: true,
      renderCell: (value, row) => (
        <MuiLink
          component={Link}
          to={`/work/projects/${row.id}`}
          sx={{
            textDecoration: 'underline',
            color: 'text.primary',
            '&:visited': { color: 'text.primary' },
          }}
        >
          {value}
        </MuiLink>
      ),
    },
    { id: 'timeLeft', label: 'Time Left', dataKey: 'timeLeft', sortable: true },
    { id: 'status', label: 'Status', dataKey: 'status', sortable: true },
  ];
}

/**
 * Returns the first selectable ISO week for a given month.
 *
 * @param {Array} weeks - Output of buildMonthWeeks.
 * @param {Date} today - Normalized "today" date (00:00).
 * @returns {{ year: number, week: number } | null}
 */
function getFirstSelectableWeek(weeks, today) {
  const week = weeks.find((w) => w.range.end > today);
  return week ? week.isoWeek : null;
}

/**
 * Finds the first selectable ISO week in a given month.
 */
function findFirstWeekOfMonth(year, month, today) {
  const weeks = buildMonthWeeks(year, month);
  return getFirstSelectableWeek(weeks, today);
}

/**
 * Finds the next month that has at least one selectable week.
 */
function findNextSelectableMonth(year, month, today) {
  let y = year;
  let m = month;

  while (true) {
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }

    const nextWeek = findFirstWeekOfMonth(y, m, today);
    if (nextWeek) {
      return { year: y, month: m, week: nextWeek };
    }
  }
}

export default function EmployeeProjectList() {
  const { user } = useAuth();
  const employeeId = user.employee_id;

  const { projects, loading, fetchProjectsByWeek, setProjects } =
    useEmployeeProjectList(employeeId);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const currentIsoWeek = getIsoWeek(today);

  const [year, setYear] = useState(today.getFullYear());
  const [activeMonth, setActiveMonth] = useState(today.getMonth());
  const [selectedIsoWeek, setSelectedIsoWeek] = useState(currentIsoWeek);

  const prevMonthRef = useRef({ year, month: activeMonth });

  const weeks = useMemo(() => buildMonthWeeks(year, activeMonth), [year, activeMonth]);

  const selectedWeekIndex = weeks.findIndex(
    (w) => w.isoWeek.year === selectedIsoWeek.year && w.isoWeek.week === selectedIsoWeek.week,
  );

  const handlePrevWeek = () => {
    for (let i = selectedWeekIndex - 1; i >= 0; i--) {
      if (weeks[i].range.end > today) {
        setSelectedIsoWeek(weeks[i].isoWeek);
        return;
      }
    }
  };

  const handleNextWeek = () => {
    for (let i = selectedWeekIndex + 1; i < weeks.length; i++) {
      if (weeks[i].range.end > today) {
        setSelectedIsoWeek(weeks[i].isoWeek);
        return;
      }
    }

    const next = findNextSelectableMonth(year, activeMonth, today);

    setYear(next.year);
    setActiveMonth(next.month);
    setSelectedIsoWeek(next.week);
  };

  const hasPrevSelectableWeek = weeks.slice(0, selectedWeekIndex).some((w) => w.range.end > today);

  const hasNextSelectableWeek = weeks.slice(selectedWeekIndex + 1).some((w) => w.range.end > today);

  useEffect(() => {
    if (isPastMonth(year, activeMonth)) {
      setActiveMonth(currentMonth);
      setYear(currentYear);
    }
  }, [year, activeMonth, currentMonth, currentYear]);

  useEffect(() => {
    fetchProjectsByWeek(selectedIsoWeek.week);
  }, [fetchProjectsByWeek, selectedIsoWeek]);

  useEffect(() => {
    const prev = prevMonthRef.current;

    const monthChanged = prev.year !== year || prev.month !== activeMonth;

    if (!monthChanged) return;

    const nextWeek = getFirstSelectableWeek(weeks, today);

    if (nextWeek) {
      setSelectedIsoWeek(nextWeek);
    }

    prevMonthRef.current = { year, month: activeMonth };
  }, [year, activeMonth, weeks, today]);

  useEffect(() => {
    logger.log({
      message: 'EmployeeProjectList',
      data: {
        projects,
        loading,
      },
    });
  }, [projects, loading]);

  const hasRunningTasks = useMemo(
    () =>
      projects.some((project) =>
        project.tasks.some(
          (task) => task.currentWorkingStatus === TASK_STATUS.IN_PROGRESS && task.lastPlay,
        ),
      ),
    [projects],
  );
  const now = useNow(hasRunningTasks);

  const columns = useMemo(() => getProjectListColumns(), []);
  const tableData = useMemo(() => {
    return projects.map((project) => {
      const minutesLeft = calculateProjectTimeLeft(project.tasks, now);

      return {
        ...project,
        timeLeft: formatTimeLeft(minutesLeft),
      };
    });
  }, [projects, now]);

  const handleTaskToggle = async (projectId, taskId) => {
    const now = new Date();

    const project = projects.find((p) => p.id === projectId);
    const task = project?.tasks.find((t) => t.id === taskId);

    if (!task || isTaskTerminal(task.currentWorkingStatus)) return;

    const isRunning = isTaskRunning(task);

    const payload = isRunning
      ? {
          intent: 'PAUSE',
          taskId,
          currentWorkingStatus: TASK_STATUS.IN_PROGRESS,
          timeUsed: accumulateTimeUsed(task.timeUsed, task.lastPlay, now),
          lastPlay: null,
        }
      : {
          intent: 'PLAY',
          taskId,
          currentWorkingStatus: TASK_STATUS.IN_PROGRESS,
          lastPlay: now.toISOString(),
        };

    let prevProjects;

    setProjects((prev) => {
      prevProjects = prev;

      return prev.map((p) => {
        if (p.id !== projectId) return p;

        return {
          ...p,
          tasks: p.tasks.map((t) => {
            if (t.id !== taskId) return t;

            return isRunning
              ? {
                  ...t,
                  timeUsed: payload.timeUsed,
                  lastPlay: null,
                }
              : {
                  ...t,
                  currentWorkingStatus: TASK_STATUS.IN_PROGRESS,
                  lastPlay: payload.lastPlay,
                };
          }),
        };
      });
    });

    try {
      await updateTaskStatus(employeeId, taskId, payload);
    } catch (error) {
      setProjects(prevProjects);

      logger.error({
        message: 'Failed to update task working state',
        error,
        payload,
      });
    }
  };

  return (
    <div className='column gap-3'>
      <SectionTitle
        title='MY DAILY TASKS'
        action={
          <MonthYearPickerAction
            year={year}
            month={activeMonth}
            minYear={currentYear}
            minMonth={currentMonth}
            onChange={({ year, month }) => {
              setYear(year);
              setActiveMonth(month);
            }}
          />
        }
      />

      <div className='column gap-5'>
        <div className='row gap-5 row--between'>
          <IconButton
            type='button'
            onClick={handlePrevWeek}
            disabled={!hasPrevSelectableWeek}
            sx={{
              opacity: !hasPrevSelectableWeek ? 0.5 : 1,
              cursor: !hasPrevSelectableWeek ? 'default' : 'pointer',
            }}
          >
            <Icon icon='mingcute:left-line' />
          </IconButton>

          {weeks.map((week) => {
            const isSelected =
              week.isoWeek.year === selectedIsoWeek.year &&
              week.isoWeek.week === selectedIsoWeek.week;

            const isPast = week.range.end < today;

            return (
              <div
                key={`${week.isoWeek.year}-${week.isoWeek.week}`}
                className='column gap-1 items-center'
              >
                <Button
                  type='button'
                  onClick={() => setSelectedIsoWeek(week.isoWeek)}
                  disabled={isPast}
                  sx={{
                    px: 2,
                    py: 1,
                    width: 120,
                    borderRadius: 4,
                    backgroundColor: (theme) =>
                      isSelected ? theme.palette.header.main : alpha(theme.palette.grey[400], 0.4),
                    opacity: isPast ? 0.5 : 1,
                    cursor: isPast ? 'default' : 'pointer',
                  }}
                >
                  {week.label}
                </Button>
                <span>
                  <span>
                    {formatOrdinalDay(week.range.start.getUTCDate())} -{' '}
                    {formatOrdinalDay(week.range.end.getUTCDate())}
                  </span>
                </span>
              </div>
            );
          })}

          <IconButton
            type='button'
            onClick={handleNextWeek}
            disabled={!hasNextSelectableWeek}
            sx={{
              opacity: !hasNextSelectableWeek ? 0.5 : 1,
              cursor: !hasNextSelectableWeek ? 'default' : 'pointer',
            }}
          >
            <Icon icon='mingcute:right-line' />
          </IconButton>
        </div>

        <ExpandableTable
          columns={columns}
          data={tableData}
          loading={loading}
          emptyMessage='No tasks assigned for this week'
          renderExpandedRow={(project) => (
            <ProjectListExpandableRow project={project} onTaskToggle={handleTaskToggle} />
          )}
          hideHeader
        />
      </div>
    </div>
  );
}

// {
//   "id": 1,
//   "code": "JOB020226",
//   "name": "Test Project",
//   "status": "Not Started",
//   "department": "drawing",
//   "tasks": [
//       {
//           "id": 3,
//           "name": "S6.1 Drawing",
//           "priority": "Medium",
//           "totalTime": "01:00:00",
//           "timeUsed": "00:00:00",
//           "currentWorkingStatus": "Assigned",
//           "lastPlay": null,
//           "lastPause": null,
//           "note": "Test"
//       },
//       {
//           "id": 4,
//           "name": "S7.1 Drawing",
//           "priority": "Medium",
//           "totalTime": "01:00:00",
//           "timeUsed": "00:00:00",
//           "currentWorkingStatus": "Assigned",
//           "lastPlay": null,
//           "lastPause": null,
//           "note": "Test"
//       }
//   ],
//   "timeLeft": "02hrs : 00mins left"
// }

function ProjectListExpandableRow({ project, onTaskToggle }) {
  return (
    <div className='column gap-3'>
      {project.tasks.map((task) => (
        <CollapsibleCard
          key={task.id}
          defaultExpanded
          colorVariant='default'
          borderVariant='outlined'
        >
          <div className='column'>
            <div className='row gap-8'>
              <div className='row flex-1'>
                <StyledGrid columns={3} sx={{ width: '100%' }}>
                  <StyledGridItem columnIndex={0}>
                    <TaskName taskName={task.name} />
                  </StyledGridItem>
                  <StyledGridItem columnIndex={1}>
                    <span>Job code</span>
                    <span>{project.code}</span>
                  </StyledGridItem>
                  <StyledGridItem columnIndex={2}>
                    <span>Project name</span>
                    <span>{project.name}</span>
                  </StyledGridItem>
                </StyledGrid>
              </div>

              {!isTaskTerminal(task.currentWorkingStatus) && (
                <div className='row gap-3'>
                  <div className='column gap-0'>
                    <IconButton
                      type='button'
                      size='small'
                      onClick={() => onTaskToggle(project.id, task.id)}
                      sx={{ color: 'primary.light' }}
                    >
                      <Icon
                        icon={
                          isTaskRunning(task)
                            ? 'material-symbols:pause'
                            : 'material-symbols:play-arrow'
                        }
                      />
                    </IconButton>
                    <span className='text-xs self-center'>
                      {isTaskRunning(task) ? 'Pause' : 'Play'}
                    </span>
                  </div>

                  {/* <div className='column gap-0'>
                    <IconButton
                      type='button'
                      size='small'
                      onClick={() => onTaskSubmit(project.id, task.id)}
                      sx={{ color: 'primary.light' }}
                      disabled={!canSubmitTask(task)}
                    >
                      <Icon icon='ri:stop-fill' />
                    </IconButton>
                    <span className='text-xs self-center'>Complete</span>
                  </div> */}
                </div>
              )}
            </div>

            <StyledGrid columns={3}>
              <StyledGridItem columnIndex={0}>
                <span>Work timeline</span>
                <span>{formatTimeString(task.totalTime)}</span>
              </StyledGridItem>
              <StyledGridItem columnIndex={1}>
                <span>Priority</span>
                <Typography
                  component='span'
                  sx={{
                    color: PRIORITY_COLORS[task.priority],
                    fontWeight: 600,
                  }}
                >
                  {PRIORITY_LABELS[task.priority]}
                </Typography>
              </StyledGridItem>
              <StyledGridItem columnIndex={2}>
                <span className='self-start'>Inward documents</span>
                <div className='column gap-1'>
                  {task.documentsShared?.length === 0 && (
                    <span className='unavailable-message text-sm'>No inward documents shared</span>
                  )}
                  {task.documentsShared?.map((document) => (
                    <MuiLink
                      key={document.id}
                      href={document.url}
                      target='_blank'
                      sx={{
                        textDecoration: 'underline',
                        color: (theme) => alpha(theme.palette.primary.light, 0.5),
                      }}
                    >
                      {document.name}
                    </MuiLink>
                  ))}
                </div>
              </StyledGridItem>
            </StyledGrid>

            <StyledGrid columns={1}>
              <StyledGridItem columnIndex={0}>
                <span>Note/Comment</span>
                <span>{task.note}</span>
              </StyledGridItem>
            </StyledGrid>
          </div>
        </CollapsibleCard>
      ))}
    </div>
  );
}
